// Ad domain — the MANAGEMENT contract for ads, shared across the advertiser
// portal, the admin panel, and the backend. This is the API shape (ISO string
// timestamps). The backend persists it as `AdDocument` (Firestore Timestamps,
// in models/AdModel); the leaner render/serve shape is `AdResponse` (api.ts).
// Schemas are authoritative; the TS types are derived via z.infer<>.

import { z } from 'zod';
import { AdPlacementSchema } from './api';

export const AdStatusSchema = z.enum(['draft', 'pending_review', 'approved', 'rejected']);
export type AdStatus = z.infer<typeof AdStatusSchema>;

// Targeting was deliberately simplified to two reliable axes: medical specialty and professional
// title (career stage). The old interests/pgyLevels/locations dimensions were dropped — interests
// were freeform and almost never matched the canonical topic vocab, PGY overlapped professional
// title (and only covered residents), and location matching was brittle. Specialty targeting can
// also be set at the campaign level (campaign.targetSpecialties) as an inherited default.
export const AdTargetingSchema = z.object({
  specialties: z.array(z.string()).optional(),
  professionalTitles: z.array(z.string()).optional(),
});
export type AdTargeting = z.infer<typeof AdTargetingSchema>;

/**
 * An ad as managed by the advertiser portal + admin panel. Each ad is
 * individually moderated (`status`) before it serves; campaigns are not
 * reviewed (see CampaignStatus). `placement` names where it renders — note the
 * lightweight serve shape (`AdResponse`) historically calls the same value
 * `type` on the wire.
 */
export const AdSchema = z.object({
  id: z.string(),
  campaignId: z.string(),
  title: z.string(),
  // The stored shape stays a plain string: documents written before the request contract
  // below existed, and the content seeder's placeholder image, must still parse on read.
  // The contract is enforced where the value enters: the create/update request schemas.
  imageUrl: z.string().optional(),
  text: z.string().optional(),
  ctaText: z.string().optional(),
  targetUrl: z.string().optional(),
  placement: AdPlacementSchema,
  targeting: AdTargetingSchema.optional(),
  weight: z.number().optional(),
  priority: z.number(),
  enabled: z.boolean(),
  status: AdStatusSchema,
  rejectionReason: z.string().optional(),
  reviewedBy: z.string().optional(),
  reviewedAt: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Ad = z.infer<typeof AdSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Ad-creative request contracts — the single source of truth for the ad
// field-shape rules that were DUPLICATED (and had drifted) across the backend's
// `src/utils/adValidation.ts` and the advertiser's `src/lib/adValidation.ts`.
// `placement` uses the canonical `AdPlacementSchema`; the old VALID_PLACEMENTS
// carried a dead `'sidebar'` no client can produce (the advertiser picker is
// typed to `AdPlacement`), so it's gone. IMAGE validation (the uploaded file's
// type and size) stays imperative — a shape schema can't inspect bytes — but
// its limits are declared here beside the field limits, for the same reason.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The field bounds, declared once. The request schema below is built from them, and the
 * advertiser form reads them for its input hints and counters, where they used to be restated.
 */
export const AD_FIELD_LIMITS = {
  title: { min: 3, max: 100 },
  text: { max: 300 },
  ctaText: { max: 30 },
  targetUrl: { max: 2048 },
  imageUrl: { max: 2048 },
  weight: { min: 1, max: 100 },
  priority: { min: 1, max: 10 },
} as const;

/**
 * What an uploaded ad creative may be. The backend's upload middleware enforces the size and its
 * image validator the type; the advertiser form checks both before it uploads. The size used to be
 * written in three places and the list in two.
 */
const AD_IMAGE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
};

export const AD_IMAGE_LIMITS = {
  maxSizeBytes: 5 * 1024 * 1024,
  /**
   * The extension an uploaded creative is stored under, per accepted MIME type. Both upload
   * routes derive the stored name from this map, never from the client's filename: the name
   * becomes the ad's `imageUrl`, which must satisfy the contract below.
   */
  extensionByMimeType: AD_IMAGE_EXTENSIONS,
  allowedMimeTypes: Object.keys(AD_IMAGE_EXTENSIONS),
};

/**
 * What an ad's `imageUrl` may look like. The system only ever produces two values: the public
 * object URL on storage.googleapis.com that the upload routes return in production, and the
 * Storage emulator's media URL locally. Every consumer then places the string inside CSS or
 * HTML (the previews' image elements, the clients' <img src>), so the contract admits those two
 * shapes and nothing that could close a CSS `url()` or an attribute: no parentheses, quotes,
 * semicolons, braces, whitespace, backslashes or angle brackets, and an http(s) scheme only.
 * No dot segment either, raw (`..`) or percent-encoded (`%2e`): a browser resolves those away,
 * so a path that starts under one folder could end up fetching from another. Neither appears
 * in an emitted URL. The backend additionally pins the URL to its own bucket
 * (utils/adImageUrl); this is the shape every client can check.
 */
export const AD_IMAGE_URL_PATTERN =
  /^(?!.*\.\.)(?!.*%2[eE])https?:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?\/[A-Za-z0-9/._%?=&-]+$/;

export const AdImageUrlSchema = z
  .string()
  .max(AD_FIELD_LIMITS.imageUrl.max, 'Image URL is too long')
  .regex(AD_IMAGE_URL_PATTERN, 'Image URL must be an uploaded image');

const WEIGHT_RANGE = `Weight must be between ${AD_FIELD_LIMITS.weight.min} and ${AD_FIELD_LIMITS.weight.max}`;
const PRIORITY_RANGE = `Priority must be between ${AD_FIELD_LIMITS.priority.min} and ${AD_FIELD_LIMITS.priority.max}`;

/**
 * Fields an advertiser submits to CREATE an ad. The server sets `campaignId`,
 * `status`, and `enabled` — they're not part of this request.
 */
export const AdCreateRequestSchema = z.object({
  title: z
    .string()
    .trim()
    .min(
      AD_FIELD_LIMITS.title.min,
      `Title must be at least ${AD_FIELD_LIMITS.title.min} characters`,
    )
    .max(
      AD_FIELD_LIMITS.title.max,
      `Title must be ${AD_FIELD_LIMITS.title.max} characters or fewer`,
    ),
  placement: AdPlacementSchema,
  imageUrl: AdImageUrlSchema.optional(),
  text: z
    .string()
    .max(
      AD_FIELD_LIMITS.text.max,
      `Ad text must be ${AD_FIELD_LIMITS.text.max} characters or fewer`,
    )
    .optional(),
  ctaText: z
    .string()
    .max(
      AD_FIELD_LIMITS.ctaText.max,
      `CTA text must be ${AD_FIELD_LIMITS.ctaText.max} characters or fewer`,
    )
    .optional(),
  targetUrl: z
    .string()
    .max(AD_FIELD_LIMITS.targetUrl.max, 'Target URL is too long')
    .regex(/^https?:\/\//, 'Target URL must start with http:// or https://')
    .optional()
    .or(z.literal('')),
  targeting: AdTargetingSchema.optional(),
  weight: z
    .number()
    .int()
    .min(AD_FIELD_LIMITS.weight.min, WEIGHT_RANGE)
    .max(AD_FIELD_LIMITS.weight.max, WEIGHT_RANGE)
    .optional(),
  priority: z
    .number()
    .int()
    .min(AD_FIELD_LIMITS.priority.min, PRIORITY_RANGE)
    .max(AD_FIELD_LIMITS.priority.max, PRIORITY_RANGE)
    .optional(),
});
export type AdCreateRequest = z.infer<typeof AdCreateRequestSchema>;

/**
 * Fields an advertiser may change on an existing ad — all optional, plus the
 * advertiser-toggleable `enabled`. An absent field is left unchanged; the optional
 * creative fields and the targeting also accept null, which CLEARS them (JSON has no
 * undefined, so a form needs a value that says "now empty").
 */
export const AdUpdateRequestSchema = AdCreateRequestSchema.partial().extend({
  imageUrl: AdCreateRequestSchema.shape.imageUrl.nullable(),
  text: AdCreateRequestSchema.shape.text.nullable(),
  ctaText: AdCreateRequestSchema.shape.ctaText.nullable(),
  targetUrl: AdCreateRequestSchema.shape.targetUrl.nullable(),
  targeting: AdCreateRequestSchema.shape.targeting.nullable(),
  enabled: z.boolean().optional(),
});
export type AdUpdateRequest = z.infer<typeof AdUpdateRequestSchema>;
